import { useEffect, useRef, useState } from "react";
import { Routes, Route, NavLink, Navigate, useLocation } from "react-router-dom";
import { Home, LayoutGrid, ScrollText, UserRound } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { SupportList } from "@/pages/Support";
import { MLogin, LockSetup, LockScreen } from "./Auth";
import { MHome } from "./Home";
import { MMoveToBank } from "./MoveToBank";
import { MFundRequest, MFundHistory } from "./Fund";
import { MReports, MNotifications } from "./Reports";
import { MServices, MServiceEmbed, MProfile } from "./Profile";
import { Screen } from "./ui";
import { getLock, LOCK_AFTER_MS } from "./lock";

const TABS = [["/app", "Home", Home], ["/app/services", "Services", LayoutGrid], ["/app/reports", "Reports", ScrollText], ["/app/profile", "Profile", UserRound]];

function TabBar() {
  const loc = useLocation();
  if (/\/app\/(move|fund|svc|notifications|support|fund-history)/.test(loc.pathname)) return null;
  return (
    <nav className="fixed inset-x-0 bottom-0 z-30 grid grid-cols-4 border-t border-black/5 bg-white/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md" data-testid="m-tabbar">
      {TABS.map(([to, label, Icon]) => (
        <NavLink key={to} to={to} end={to === "/app"} className={({ isActive }) => `flex h-16 flex-col items-center justify-center gap-1 text-[11px] font-bold ${isActive ? "text-[#F26B1D]" : "text-slate-500"}`} data-testid={`m-tab-${label.toLowerCase()}`}>
          <Icon className="h-6 w-6" />{label}
        </NavLink>
      ))}
    </nav>
  );
}

function useAppLock(user) {
  const [locked, setLocked] = useState(true);
  const [cfg, setCfg] = useState(() => (user ? getLock(user.id) : null));
  const hiddenAt = useRef(0);
  useEffect(() => { setCfg(user ? getLock(user.id) : null); }, [user]);
  useEffect(() => {
    const onVis = () => {
      if (document.hidden) hiddenAt.current = Date.now();
      else if (hiddenAt.current && Date.now() - hiddenAt.current > LOCK_AFTER_MS) setLocked(true);
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);
  return { locked, setLocked, cfg, setCfg };
}

export default function MobileApp() {
  const { user, logout } = useAuth();
  const { locked, setLocked, cfg, setCfg } = useAppLock(user || null);
  useEffect(() => {
    document.title = "Retailer App";
    document.body.classList.add("m-app");
    return () => document.body.classList.remove("m-app");
  }, []);
  if (user === null) return <div className="grid min-h-screen place-items-center bg-[#F6F5F1] text-sm text-slate-500" data-testid="m-loading">Loading…</div>;
  if (!user) return <MLogin />;
  if (user.role !== "retailer") {
    return (
      <div className="flex min-h-screen flex-col justify-center gap-4 bg-[#F6F5F1] px-6 text-center" data-testid="m-not-retailer">
        <p className="text-sm text-slate-600">This app is for retailers only. Please use the web portal.</p>
        <button className="font-bold text-[#0B1F33]" onClick={logout} data-testid="m-not-retailer-logout">Logout</button>
      </div>
    );
  }
  if (!cfg) return <LockSetup user={user} onDone={() => { setCfg(getLock(user.id)); setLocked(false); }} />;
  if (locked) return <LockScreen user={user} cfg={cfg} onUnlock={() => setLocked(false)} />;
  return (
    <div className="mx-auto min-h-screen max-w-md bg-white text-[#0F2C47]" data-testid="m-app">
      <Routes>
        <Route index element={<MHome />} />
        <Route path="services" element={<MServices />} />
        <Route path="svc/:key" element={<MServiceEmbed />} />
        <Route path="move" element={<MMoveToBank />} />
        <Route path="fund" element={<MFundRequest />} />
        <Route path="fund-history" element={<MFundHistory />} />
        <Route path="reports" element={<MReports />} />
        <Route path="notifications" element={<MNotifications />} />
        <Route path="support" element={<Screen title="Support"><div className="m-embed"><SupportList /></div></Screen>} />
        <Route path="profile" element={<MProfile onRelock={() => setCfg(null)} />} />
        <Route path="*" element={<Navigate to="/app" replace />} />
      </Routes>
      <TabBar />
    </div>
  );
}
